import { Component, ElementRef, OnInit } from "@angular/core";
import { takeUntil, BehaviorSubject, Observable, Subject, tap, of, catchError, switchMap } from "rxjs";
import { DestroyableMixin } from "apps-module/app/shared-library/mixins/destroyable.mixin";
import { LockMixin } from "apps-module/app/shared-library/mixins/lock.mixin";
import { ConfigurationService } from "apps-module/app/shared-library/services/configuration/configuration.service";
import { BrowserWindowService } from "apps-module/app/shared-library/services/environment/browser-window.service";
import { EmptyInstance } from "core-library/mixins/empty-instance";
import { SearchConfigurationModel } from "apps-module/app/entry-modules/content-search/models/search-configuration.model";
import { FormSearchParametersService } from "apps-module/app/entry-modules/content-search/services/form-search-parameters.service";
import { SearchService } from "apps-module/app/entry-modules/content-search/services/search.service";
import { QuerySearchParametersService } from "apps-module/app/entry-modules/content-search/services/query-search-parameters.service";
import { SearchForm } from "apps-module/app/entry-modules/content-search/forms/search.form";
import { FormSearchParametersModel } from "apps-module/app/entry-modules/content-search/models/form-search-parameters.model";
import { SearchResultModel } from "apps-module/app/entry-modules/content-search/models/search-result.model";

@Component({
    selector: "[pcg-ng-content-search]",
    templateUrl: "./content-search.component.html",
    providers: [ConfigurationService, FormSearchParametersService, SearchService, QuerySearchParametersService]
})
export class ContentSearchComponent extends DestroyableMixin(LockMixin(EmptyInstance)) implements OnInit {
    public configuration: SearchConfigurationModel;
    public searchForm: SearchForm;

    public searchParameters$: BehaviorSubject<FormSearchParametersModel>;
    public searchResult$: Observable<SearchResultModel>;
    public searchInProgress$ = new Subject<boolean>();
    public statusMessage$ = new Subject<string>();

    public constructor(
        private _domElement: ElementRef,
        private _browserWindowService: BrowserWindowService,
        private _configurationService: ConfigurationService<SearchConfigurationModel>,
        private _searchParametersFactory: FormSearchParametersService,
        private _searchService: SearchService,
        private _queryStringSetterService: QuerySearchParametersService
    ) {
        super();
    }

    public ngOnInit(): void {
        this._configurationService.initialize(this._domElement);

        this.configuration = this._configurationService.getConfiguration();

        const queryString = this._browserWindowService.getQueryString();
        const searchParameters = this._searchParametersFactory.create(queryString, this.configuration);
        this.searchParameters$ = new BehaviorSubject<FormSearchParametersModel>(searchParameters);
        this.searchForm = SearchForm.createForm(searchParameters);

        this.searchForm.onFormSubmitted
            .subscribe(params => this.searchParameters$.next(params));

        this.searchResult$ = this.searchParameters$.pipe(
            tap((params) => {
                this.searchInProgress$.next(true);
                this._queryStringSetterService.set(params);
            }),
            switchMap(params => this._searchService.search(params)),
            tap((result) => {
                this.statusMessage$.next(this.createResultMessage(result));
            }),
            catchError(() => {
                this.statusMessage$.next("An error has occurred. Please try again or contact the system administrator if the problem persists.");
                return of({} as SearchResultModel);
            }),
            tap(() => this.searchInProgress$.next(false)),
            takeUntil(this.onDestroy)
        );
    }

    private createResultMessage(result: SearchResultModel): string {
        const message = `Found <span>${result.results.totalItems}</span> results<span> for \"<strong>${this.searchParameters$.value.searchText}</strong>\" query</span>.`;
        const extraLink = !!this.configuration.extraLinkUrl && !!this.configuration.extraLinkText
            ? `<a class="pcg-contrast__link" href="${this.configuration.extraLinkUrl}" target="${this.configuration.extraLinkTarget}">${this.configuration.extraLinkText}</a>`
            : "";
        const extraText = `<span> ${this.configuration.extraText ?? ""} ${extraLink}</span>`;
        return message + extraText;
    }
}
